import React from "react";
import {
  useNavigate,
  useNavigationType,
  useSearchParams,
} from "react-router-dom";
import MainAppBar from "../../components/atoms/appbar/MainAppbar";
import CompetitionListCard from "../../components/layout/competition/CompetitionListCard";

import { useInfiniteQuery } from "@tanstack/react-query";
import { useTournamentListInfinite } from "../../@apis/hooks/useTournament";
import { saveScrollPosition } from "../../hooks/useMainScrollRestoration";
import type { TournamentApplyStatus } from "../../@apis/dto/response/torunament.response";

// 상태 필터 (서버 status 파라미터로 전달)
const STATUS_FILTERS: {
  label: string;
  value: TournamentApplyStatus | undefined;
}[] = [
  { label: "전체", value: undefined },
  { label: "접수중", value: "APPLYING" },
  { label: "접수예정", value: "PRE_APPLY" },
  { label: "접수종료", value: "APPLY_END" },
  { label: "대회종료", value: "END" },
];

export default function Competition() {
  const navigate = useNavigate();

  // 필터는 URL 쿼리에 보관한다. 하단 탭으로 새로 들어오면 파라미터가 없어
  // 초기 상태가 되고, 상세에서 뒤로 오면 브라우저가 URL을 그대로 복원해 준다.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlKeyword = searchParams.get("q") ?? "";
  const statusValue = STATUS_FILTERS.find(
    (f) => f.value !== undefined && f.value === searchParams.get("status"),
  )?.value;
  const statusLabel =
    STATUS_FILTERS.find((f) => f.value === statusValue)?.label ?? "전체";
  const myOnly = searchParams.get("mine") === "1";

  // 타이핑/필터 변경으로 히스토리가 쌓이지 않도록 항상 현재 항목을 교체한다.
  const updateParams = React.useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // 스크롤/로드된 페이지 수 복원. 상세에서 결제 완료 후 홈으로 나가면 플래그가
  // 남으므로, 뒤로가기(POP)로 들어온 경우에만 복원한다.
  const navigationType = useNavigationType();
  const cameFromDetailRef = React.useRef(
    navigationType === "POP" &&
      sessionStorage.getItem("competition_came_from_detail") === "true",
  );
  const savedScrollRef = React.useRef(
    cameFromDetailRef.current
      ? Number(sessionStorage.getItem("scroll_competition") ?? "0")
      : 0,
  );
  const savedPagesRef = React.useRef(
    cameFromDetailRef.current
      ? Number(sessionStorage.getItem("competition_scroll_pages") ?? "1")
      : 1,
  );
  const scrollRestoredRef = React.useRef(false);
  const restoreCleanupRef = React.useRef<(() => void) | null>(null);

  React.useEffect(() => {
    if (!cameFromDetailRef.current) {
      sessionStorage.removeItem("scroll_competition");
      sessionStorage.removeItem("competition_scroll_pages");
    }
    sessionStorage.removeItem("competition_came_from_detail");
  }, []);

  // 입력값은 즉시 반응해야 하므로 로컬 상태로 두고, 디바운스 후 URL에 반영한다.
  const [keyword, setKeyword] = React.useState(urlKeyword);

  React.useEffect(() => {
    const trimmed = keyword.trim();
    if (trimmed === urlKeyword) return;
    const t = setTimeout(() => {
      updateParams((params) => {
        if (trimmed) params.set("q", trimmed);
        else params.delete("q");
      });
    }, 300);
    return () => clearTimeout(t);
  }, [keyword, urlKeyword, updateParams]);

  // 같은 경로로 다시 진입해 파라미터만 초기화되는 경우 입력창도 맞춰 준다.
  // 입력 중 공백까지 지워지지 않도록 다듬은 값이 같으면 그대로 둔다.
  React.useEffect(() => {
    setKeyword((prev) => (prev.trim() === urlKeyword ? prev : urlKeyword));
  }, [urlKeyword]);

  const filters = React.useMemo(
    () => ({
      name: urlKeyword || undefined,
      status: statusValue,
      isMyApply: myOnly || undefined,
    }),
    [urlKeyword, statusValue, myOnly],
  );

  const {
    data,
    isSuccess,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...useTournamentListInfinite(filters),
  });

  const pages = data?.pages ?? [];
  const tournamentList = pages.flatMap(
    (page) => page.data?.tournamentList ?? [],
  );
  const firstPageItems = pages[0]?.data?.tournamentList;

  // 검색어/상태/내 신청 중 하나라도 적용된 상태
  const isFiltering = urlKeyword !== "" || statusValue !== undefined || myOnly;

  // 복원 1) 저장된 페이지 수만큼 로드
  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (pages.length >= savedPagesRef.current) return;
    if (!hasNextPage || isFetchingNextPage) return;
    fetchNextPage();
  }, [pages.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // 복원 2) 충분한 페이지가 로드되면 스크롤 위치 복원
  const enoughPagesLoaded =
    pages.length >= savedPagesRef.current || !hasNextPage;
  React.useEffect(() => {
    if (!cameFromDetailRef.current || scrollRestoredRef.current) return;
    if (!enoughPagesLoaded || !firstPageItems?.length) return;

    scrollRestoredRef.current = true;

    const saved = savedScrollRef.current;
    const mainEl = document.querySelector("main") as HTMLElement | null;
    if (!saved || !mainEl) return;

    const apply = () =>
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          mainEl.scrollTop = saved;
        }),
      );

    apply();
    // 이미지/레이아웃 변화로 높이가 바뀌어도 일정 시간 위치를 재적용
    const observer = new ResizeObserver(apply);
    observer.observe(mainEl);
    const timer = setTimeout(() => observer.disconnect(), 800);

    // 재렌더로 이 이펙트가 다시 실행돼도 옵저버를 끊지 않도록 cleanup은 ref에만 보관
    restoreCleanupRef.current = () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [enoughPagesLoaded, firstPageItems?.length]);

  // 언마운트 시에만 복원 옵저버 정리
  React.useEffect(() => () => restoreCleanupRef.current?.(), []);

  // 무한 스크롤: 하단 감지 시 다음 페이지 로드
  const loadMoreRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const el = loadMoreRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    // 검색바 높이가 유동적이라 고정값 계산 대신 남는 높이를 목록 영역이 채우도록 한다.
    <div className="flex min-h-full flex-col">
      <MainAppBar title="대회 접수" />

      {/* 검색 */}
      <div className="px-[16px] py-[20px] bg-white">
        <div className="flex items-center gap-[12px] px-[8px] py-[10px] rounded-[10px] border border-[#d1d1d6]">
          <img className="size-5" src="/icons/ic-search.webp" alt="검색" />
          <input
            className="flex-1 min-w-0 text-sm text-black placeholder:text-[#c7c7cc] outline-none bg-transparent"
            placeholder="대회명 검색"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          {keyword && (
            <button
              type="button"
              aria-label="검색어 지우기"
              onClick={() => setKeyword("")}
              className="shrink-0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1rem"
                height="1rem"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4.26927 12.6668L3.33594 11.7335L7.06927 8.00016L3.33594 4.26683L4.26927 3.3335L8.0026 7.06683L11.7359 3.3335L12.6693 4.26683L8.93594 8.00016L12.6693 11.7335L11.7359 12.6668L8.0026 8.9335L4.26927 12.6668Z"
                  fill="#8E8E93"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="h-px w-full bg-[#e5e5ea]" />

      {/* 필터 + 목록 */}
      <section className="flex-1 bg-[#f2f2f7] px-[16px] py-[20px] flex flex-col gap-[20px]">
        <div className="flex items-center justify-between">
          <div className="relative">
            <select
              value={statusLabel}
              onChange={(e) => {
                const next = STATUS_FILTERS.find(
                  (f) => f.label === e.target.value,
                )?.value;
                updateParams((params) => {
                  if (next) params.set("status", next);
                  else params.delete("status");
                });
              }}
              className="appearance-none rounded-[10px] border border-[#d1d1d6] bg-white px-[16px] py-[12px] text-sm font-medium text-black pr-[36px]"
            >
              {STATUS_FILTERS.map(({ label }) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </select>
            <span className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 w-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="1rem"
                height="1rem"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M4 6L8 10L12 6"
                  stroke="#1E1E1E"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </div>

          <button
            type="button"
            onClick={() =>
              updateParams((params) => {
                if (myOnly) params.delete("mine");
                else params.set("mine", "1");
              })
            }
            className={
              myOnly
                ? "rounded-full border border-primary bg-primary px-[20px] py-[10px] text-[14px] font-medium text-white"
                : "rounded-full border border-[#8e8e93] bg-white px-[20px] py-[10px] text-[14px] font-medium text-[#8e8e93]"
            }
          >
            내 신청 대회만 보기
          </button>
        </div>

        {isSuccess && tournamentList.length === 0 ? (
          isFiltering ? (
            // 검색/필터 결과가 없을 때
            <div className="flex flex-1 flex-col items-center justify-center">
              <p className="text-sm text-[#8e8e93]">검색 결과가 없습니다.</p>
            </div>
          ) : (
            // 대회 자체가 없을 때
            <div className="flex flex-1 flex-col items-center justify-center text-[#ccc]">
              <img
                className="mb-2 size-12"
                src="/icons/ic-notice.webp"
                alt="대회 없음"
              />
              <p className="text-sm">대회가 없습니다.</p>
            </div>
          )
        ) : (
          <div className="flex flex-col gap-[12px]">
            {tournamentList.map((item) => (
              <CompetitionListCard
                key={item.id}
                item={item}
                onClick={() => {
                  saveScrollPosition("competition");
                  sessionStorage.setItem(
                    "competition_scroll_pages",
                    String(pages.length),
                  );
                  sessionStorage.setItem(
                    "competition_came_from_detail",
                    "true",
                  );
                  navigate(`/competition/${item.id}`);
                }}
              />
            ))}

            {/* 무한 스크롤 감지 & 로딩 */}
            <div ref={loadMoreRef} className="h-[1px]" />
            {(isFetchingNextPage || isLoading) && (
              <p className="py-[12px] text-center text-sm text-[#8e8e93]">
                불러오는 중...
              </p>
            )}
          </div>
        )}
      </section>

      {/* AI 콜봇 (플로팅 버튼 + 바텀시트) */}
      {/* <AiCallBotFab /> */}
    </div>
  );
}
